import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { Button } from '~/components/Button';
import { Checkbox } from '~/components/Checkbox';
import { Modal } from '~/components/Modal';

interface AboutAutomatedCreatorCampaignsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (options: { dontShowAgain: boolean }) => void;
}

export function AboutAutomatedCreatorCampaignsModal({
  isOpen,
  onClose,
  onConfirm,
}: AboutAutomatedCreatorCampaignsModalProps) {
  const { t } = useTranslation('marketing', {
    keyPrefix: 'automatedCreatorCampaign',
  });
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setDontShowAgain(false);
    }
  }, [isOpen]);

  return (
    <Modal onClose={onClose} isOpen={isOpen}>
      <div className="flex w-[500px] flex-col rounded-sm border pt-lg bg-level-02 border-emphasis-subtle text-default">
        <div className="px-lg pb-lg">
          <div className="mb-md flex items-center justify-between gap-md">
            <h2
              id="modal-title"
              className="min-w-0 flex-1 text-headline-md font-semibold text-white"
            >
              {t('aboutModal.title')}
            </h2>
            <Icon
              type="xmark"
              aria-label={t('aboutModal.closeAriaLabel')}
              onClick={onClose}
              size="lg"
              variant="regular"
              className="-mt-sm shrink-0 font-normal hover:cursor-pointer"
            />
          </div>
          <div className="pb-0 text-body-sm">
            <p className="pr-xl">{t('aboutModal.intro')}</p>
            <div className="mt-lg flex w-full min-w-0 flex-col gap-sm">
              <section className="w-full min-w-0 rounded-sm border p-md bg-level-02 border-emphasis-subtle">
                <h3 className="text-body-md font-semibold text-white">
                  {t('aboutModal.clipping.title')}
                </h3>
                <p className="mt-xs text-body-sm text-subtle">
                  {t('aboutModal.clipping.description')}
                </p>
              </section>
              <section className="w-full min-w-0 rounded-sm border p-md bg-level-02 border-emphasis-subtle">
                <h3 className="text-body-md font-semibold text-white">
                  {t('aboutModal.influencer.title')}
                </h3>
                <p className="mt-xs text-body-sm text-subtle">
                  {t('aboutModal.influencer.description')}
                </p>
              </section>
            </div>
          </div>
          <div className="mt-lg flex items-center justify-between gap-md">
            <Checkbox
              checked={dontShowAgain}
              onCheckedChange={(v) => setDontShowAgain(v === true)}
              label={t('aboutModal.dontShowAgain')}
              className="text-white"
            />
            <Button
              variant="primary"
              size="large"
              className="shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                onConfirm({ dontShowAgain });
              }}
            >
              {t('aboutModal.gotIt')}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

AboutAutomatedCreatorCampaignsModal.displayName =
  'AboutAutomatedCreatorCampaignsModal';
